// src/components/common/ConfirmFreeTextField.js
//
// The optional free-text field on a confirm — a backdate reason, a rename, an
// amount to apply. Sibling of ConfirmPhraseField, extracted alongside it so the
// two inputs a confirm can carry live in one place rather than inline in the
// dialog.
//
// This is DATA CAPTURE, not a gate: the text goes on to the audit payload or
// the RPC. `validate` soft-gates confirm and renders a live helper; `maxValue`
// swaps in MoneyInput, which refuses keystrokes and pastes above the cap
// instead of accepting then correcting them.
//
// Two shapes. The preset "Other…" REASON is writing: a multi-line, auto-fitting
// box (ConfirmReasonTextarea) where Enter adds nothing and never confirms.
// Any other free text (rename, amount, etc.) is a single-line input that keeps
// the parent's document-level Enter binding — one gate, one path. See
// ConfirmPhraseField for why that matters.

import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { foundation } from '../../ui/tokens';
import {
  typedConfirmInputSection,
  typedConfirmInputLabel,
  typedConfirmInputField,
} from '../../ui/confirmDialogStyles';
import {
  typedConfirmPhraseInputProps,
  useTypedConfirmPhraseInput,
} from './typedConfirmPhraseInput';
import MoneyInput from './MoneyInput';
import ConfirmReasonTextarea from './ConfirmReasonTextarea';
import ConfirmReasonPicker, { REASON_LABEL_ID } from './ConfirmReasonPicker';

const { color, spacePx, fontSize, fontFamily } = foundation;

/**
 * True only while this confirm shows a field that owns the on-screen keyboard:
 * a plain/money input, or the preset reason's "Other…" box once revealed. The
 * preset picker alone is taps, so it must not get the keyboard-sized height cap.
 */
export function freeTextOwnsKeyboard(freeTextInput, otherOpen) {
  if (!freeTextInput) return false;
  if (Array.isArray(freeTextInput.presets)) return Boolean(otherOpen);
  return true;
}

export default function ConfirmFreeTextField({
  freeTextInput,
  value,
  onChange,
  evaluation,
  busy = false,
  onFreeformChange,
}) {
  const intl = useIntl();
  const { blockAutofill, releaseAutofillBlock } = useTypedConfirmPhraseInput();
  const presets = Array.isArray(freeTextInput.presets) ? freeTextInput.presets : null;
  const [freeform, setFreeform] = useState(false);
  useEffect(() => {
    if (onFreeformChange) onFreeformChange(freeform);
  }, [freeform, onFreeformChange]);

  // A reason is the most common free-text field in the app, and on a phone it
  // is the one place the keyboard still has to open. Offering the three
  // reasons people actually pick turns the common case into a single tap, and
  // still captures a real reason for the audit payload. "Other…" reveals the
  // field for anything else.
  if (presets) {
    return (
      <div style={typedConfirmInputSection}>
        {freeTextInput.label ? (
          <label id={REASON_LABEL_ID} style={typedConfirmInputLabel}>{freeTextInput.label}</label>
        ) : null}
        <ConfirmReasonPicker
          showPrompt={!freeTextInput.label}
          presets={presets}
          value={value}
          freeform={freeform}
          disabled={busy}
          onPick={(text) => { setFreeform(false); onChange(text); }}
          onOther={() => { setFreeform(true); onChange(''); }}
        />
        {freeform ? (
          <ConfirmReasonTextarea
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            readOnly={blockAutofill && !busy}
            onFocus={releaseAutofillBlock}
            onMouseDown={releaseAutofillBlock}
            onTouchStart={releaseAutofillBlock}
            value={value}
            onChange={onChange}
            placeholder={freeTextInput.placeholder ?? intl.formatMessage({ id: 'confirm.reason.other', defaultMessage: 'Other…' })}
            disabled={busy}
            data-testid="confirm-reason-other-input"
          />
        ) : null}
        {evaluation.helper ? (
          <div
            role={evaluation.tone === 'danger' ? 'alert' : undefined}
            style={{
              fontSize: fontSize.xs,
              fontFamily: fontFamily.sans,
              fontWeight: evaluation.tone === 'danger' ? 500 : 400,
              color: evaluation.tone === 'danger' ? color.semantic.danger : color.neutral.textMuted,
            }}
          >
            {evaluation.helper}
          </div>
        ) : null}
      </div>
    );
  }
  const moneyMax = Number.isFinite(Number(freeTextInput.maxValue))
    ? Number(freeTextInput.maxValue)
    : null;
  const useMoneyField = moneyMax != null;

  return (
    <div style={typedConfirmInputSection}>
      {freeTextInput.label ? (
        <label style={typedConfirmInputLabel}>{freeTextInput.label}</label>
      ) : null}
      {useMoneyField ? (
        <MoneyInput
          value={value}
          onChange={onChange}
          maxValue={moneyMax}
          maxNoteOnRefuse={Boolean(freeTextInput.maxNoteOnRefuse)}
          // Cap IS the warning threshold — never amber-tint a valid in-range amount.
          softWarningThreshold={Number.POSITIVE_INFINITY}
          suppressWarningText
          compact
          placeholder={freeTextInput.placeholder ?? '0.00'}
          ariaLabel={freeTextInput.label || freeTextInput.placeholder || 'Amount'}
          disabled={busy}
        />
      ) : (
        <input
          {...typedConfirmPhraseInputProps}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          enterKeyHint="done"
          inputMode={freeTextInput.inputMode || undefined}
          readOnly={blockAutofill}
          onFocus={releaseAutofillBlock}
          onMouseDown={releaseAutofillBlock}
          onTouchStart={releaseAutofillBlock}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={typedConfirmInputField}
          placeholder={freeTextInput.placeholder ?? ''}
          disabled={busy}
        />
      )}
      {evaluation.helper ? (
        <div
          role={evaluation.tone === 'danger' ? 'alert' : undefined}
          style={{
            marginTop: spacePx[1],
            fontSize: fontSize.xs,
            fontFamily: fontFamily.sans,
            fontWeight: evaluation.tone === 'danger' ? 500 : 400,
            color: evaluation.tone === 'danger' ? color.semantic.danger : color.neutral.textMuted,
          }}
        >
          {evaluation.helper}
        </div>
      ) : null}
    </div>
  );
}
